import { createContext, useContext, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useContactNames } from '../hooks/useContactNames';
import { buildGlobalDuplicatesMap } from '../utils/contactUtils';

const DisambiguationContext = createContext({
  needsDisambiguation: () => false,
});

/**
 * Provider that computes global display name duplicates from the contact list
 * and exposes helpers to check whether a userId needs disambiguation.
 */
export const DisambiguationProvider = ({ children }) => {
  const { namesList } = useContactNames();

  // Build the duplicates map: Map<normalizedName, string[]> (only entries with >1 userId)
  // namesList already includes the current user, so no separate myDisplayName/myUserId needed
  const duplicatesMap = useMemo(
    () => buildGlobalDuplicatesMap(namesList, null, null),
    [namesList],
  );

  // Build a reverse lookup: userId -> boolean (true if that userId is in any duplicate group)
  const duplicateUserIds = useMemo(() => {
    const set = new Set();
    for (const userIds of duplicatesMap.values()) {
      for (const uid of userIds) {
        set.add(uid);
      }
    }
    return set;
  }, [duplicatesMap]);

  const needsDisambiguation = useCallback(
    (userId) => {
      if (!userId) return false;
      return duplicateUserIds.has(userId);
    },
    [duplicateUserIds],
  );

  const value = useMemo(
    () => ({ needsDisambiguation }),
    [needsDisambiguation],
  );

  return (
    <DisambiguationContext.Provider value={value}>
      {children}
    </DisambiguationContext.Provider>
  );
};

DisambiguationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to access disambiguation helpers.
 * @returns {{ needsDisambiguation: (userId: string) => boolean }}
 */
export const useDisambiguation = () => useContext(DisambiguationContext);
